import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config/api';
import { setNewRelicUserId, clearNewRelicUserId } from './utils/newrelic';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is already logged in on app start
  useEffect(() => {
    const token = getCookie('auth_token');
    if (token) {
      verifyAuthToken(token);
    } else {
      setIsLoading(false);
    }
  }, []);

  const verifyAuthToken = async (token) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/models`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.status === 200) {
        // Extract user info from token or make a separate call to get user details
        const userInfo = await getUserInfo(token);
        setIsAuthenticated(true);
        setCurrentUser(userInfo);
        setNewRelicUserId(userInfo);
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      // Token is invalid, clear it
      deleteCookie('auth_token');
      deleteCookie('refresh_token');
      setIsAuthenticated(false);
      setCurrentUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getUserInfo = async (token) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/user/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data.user;
    } catch (error) {
      console.error('Error getting user info:', error);
      // Fallback to basic user info
      return {
        email: 'user@example.com', // This would come from token or API
        role: 'user' // Default role
      };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/login`, {
        email,
        password
      });

      if (response.data.success) {
        // Store tokens in cookies
        setCookie('auth_token', response.data.tokens.accessToken, 1);
        if (response.data.tokens.refreshToken) {
          setCookie('refresh_token', response.data.tokens.refreshToken, 7);
        }
        
        setIsAuthenticated(true);
        setCurrentUser(response.data.user);
        setNewRelicUserId(response.data.user);
        return { success: true, user: response.data.user };
      } else {
        return {
          success: false,
          error: response.data.error || response.data.message || 'Login failed'
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Login failed'
      };
    }
  };

  const register = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/register`, {
        email,
        password
      });

      if (response.data.success) {
        // Store tokens in cookies
        setCookie('auth_token', response.data.tokens.accessToken, 1);
        if (response.data.tokens.refreshToken) {
          setCookie('refresh_token', response.data.tokens.refreshToken, 7);
        }
        
        setIsAuthenticated(true);
        setCurrentUser(response.data.user);
        setNewRelicUserId(response.data.user);
        return { success: true, user: response.data.user };
      } else {
        return {
          success: false,
          error: response.data.error || response.data.message || 'Registration failed'
        };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Registration failed'
      };
    }
  };

  const logout = () => {
    deleteCookie('auth_token');
    deleteCookie('refresh_token');
    setIsAuthenticated(false);
    setCurrentUser(null);
    
    // Clear NewRelic user ID on logout
    clearNewRelicUserId();
  };

  // Check if current user is admin
  const isAdmin = currentUser?.role === 'admin';

  // Cookie helper functions
  const getCookie = (name) => {
    try {
      const nameEQ = name + "=";
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
      }
      return null;
    } catch (error) {
      console.error('Error getting cookie:', error);
      return null;
    }
  };

  const setCookie = (name, value, days) => {
    try {
      const expires = new Date();
      expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
      document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
    } catch (error) {
      console.error('Error setting cookie:', error);
    }
  };

  const deleteCookie = (name) => {
    try {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    } catch (error) {
      console.error('Error deleting cookie:', error);
    }
  };

  // Helper function to get auth headers for API calls
  const getAuthHeaders = () => {
    const token = getCookie('auth_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const value = {
    isAuthenticated,
    currentUser,
    isAdmin,
    isLoading,
    login,
    register,
    logout,
    verifyAuthToken,
    getAuthHeaders
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};